import React, { useMemo, memo, useRef, useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Typography, Box, Checkbox, ButtonBase } from "@material-ui/core";
import { COLORS, LOCATION_LEVEL_ICONS } from "../constants";
import { allDescIds } from "../utils";
import { ExpandToggle } from "./ExpandToggle";
import { useStyles } from "./location-tree.style";

const QUERY_CACHE_LIMIT = 40;

function buildSearchIndex(locations) {
  const ids = [];
  const parentMap = new Map();
  const childrenMap = new Map();
  const nameMap = new Map();

  function walk(nodes, parentId) {
    for (const node of nodes) {
      ids.push(node.id);
      if (parentId != null) parentMap.set(node.id, parentId);
      nameMap.set(node.id, node.name.toLowerCase());
      const childIds = (node.children || []).map((child) => child.id);
      childrenMap.set(node.id, childIds);
      if (node.children?.length) walk(node.children, node.id);
    }
  }

  walk(locations, null);
  return { ids, parentMap, childrenMap, nameMap };
}

function cacheQueryResult(cache, query, result) {
  if (cache.size >= QUERY_CACHE_LIMIT) {
    const oldestKey = cache.keys().next().value;
    cache.delete(oldestKey);
  }
  cache.set(query, result);
}

function collectMatchingIdsFromIndex(searchIndex, query) {
  const matchingIds = [];
  for (const id of searchIndex.ids) {
    const normalizedName = searchIndex.nameMap.get(id);
    if (normalizedName?.includes(query)) matchingIds.push(id);
  }
  return matchingIds;
}

function markAncestorsVisible(searchIndex, id, visible, autoExpanded) {
  visible.add(id);
  let cur = id;
  while (cur) {
    const parentId = searchIndex.parentMap.get(cur);
    if (!parentId) break;
    visible.add(parentId);
    autoExpanded.add(parentId);
    cur = parentId;
  }
}

function markDescendantsVisible(searchIndex, id, visible, autoExpanded) {
  const descendantsToVisit = [id];
  while (descendantsToVisit.length) {
    const nodeId = descendantsToVisit.pop();
    const childIds = searchIndex.childrenMap.get(nodeId) || [];
    if (childIds.length) autoExpanded.add(nodeId);
    for (const childId of childIds) {
      if (!visible.has(childId)) visible.add(childId);
      descendantsToVisit.push(childId);
    }
  }
}

function computeSearchTreeState(searchIndex, search, queryCache) {
  const query = (search || "").trim().toLowerCase();
  if (!query) return { visibleIds: null, autoExpandedIds: null };
  const cachedResult = queryCache.get(query);
  if (cachedResult) return cachedResult;

  const matchingIds = collectMatchingIdsFromIndex(searchIndex, query);
  const visible = new Set();
  const autoExpanded = new Set();
  for (const id of matchingIds) {
    if (visible.has(id)) continue;
    markAncestorsVisible(searchIndex, id, visible, autoExpanded);
    markDescendantsVisible(searchIndex, id, visible, autoExpanded);
  }

  const result = { visibleIds: visible, autoExpandedIds: autoExpanded };
  cacheQueryResult(queryCache, query, result);
  return result;
}

function hasPartialCheck(node, checked) {
  return (node.children || []).some(
    (child) => checked?.has(child.id) || (child.children || []).some((grandchild) => checked?.has(grandchild.id))
  );
}

function getDepthClassName(classes, depth) {
  const clampedDepth = Math.min(Math.max(depth, 0), 8);
  return classes[`nodeIndent${clampedDepth}`];
}

const TreeNode = memo(function TreeNode({
  node,
  depth,
  selected,
  expanded,
  collapsedDuringSearch,
  autoExpandedIds,
  visibleIds,
  search,
  onSelect,
  onCheck,
  onToggle,
  mode,
  checked,
}) {
  const classes = useStyles();
  const query = (search || "").toLowerCase();
  if (query && visibleIds && !visibleIds.has(node.id)) return null;
  const hasQuery = query.length > 0;
  const isExplicitExpanded = expanded?.has(node.id);
  const isAutoExpanded = !!(hasQuery && autoExpandedIds?.has(node.id));
  const isCollapsedDuringSearch = !!(hasQuery && collapsedDuringSearch?.has(node.id));
  const isExpanded = !!isExplicitExpanded || (isAutoExpanded && !isCollapsedDuringSearch);
  const hasChildren = node.children?.length > 0;
  const isSelected = selected === node.id;
  const isSelectMode = mode === "select";
  const isChecked = isSelectMode ? checked?.has(node.id) : false;
  const descendantIds = isSelectMode ? allDescIds(node) : [];
  const isPartiallyChecked = isSelectMode && !isChecked && hasPartialCheck(node, checked);
  const depthClassName = getDepthClassName(classes, depth);
  const selectedClassName = isSelected ? classes.nodeRowSelected : classes.nodeRowUnselected;

  return (
    <Box>
      <Box className={`${classes.nodeRow} ${depthClassName} ${selectedClassName}`}>
        {hasChildren && (
          <ExpandToggle
            isExpanded={isExpanded}
            onToggle={(id) => onToggle?.(id, { isAutoExpanded, isExplicitExpanded: !!isExplicitExpanded })}
            nodeId={node.id}
          />
        )}
        {!hasChildren && <Box className={classes.expandSpacer} />}
        {isSelectMode && (
          <Checkbox
            checked={!!isChecked}
            indeterminate={!!isPartiallyChecked && !isChecked}
            onChange={() => onCheck?.(node.id, descendantIds)}
            onClick={(event) => event.stopPropagation()}
            size="small"
            className={classes.checkbox}
          />
        )}
        {mode === "browse" && (
          <ButtonBase
            onClick={() => onSelect?.(node.id)}
            className={classes.nodeButton}
          >
            <Typography component="span" className={classes.nodeIcon}>
              {LOCATION_LEVEL_ICONS[node.level]}
            </Typography>
            <Typography
              component="span"
              variant="body2"
              align="left"
              sx={{
                color: COLORS.body,
                fontWeight: isSelected ? 600 : 400,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {node.name}
            </Typography>
          </ButtonBase>
        )}
        {mode !== "browse" && (
          <>
            <Typography component="span" className={classes.nodeIcon}>
              {LOCATION_LEVEL_ICONS[node.level]}
            </Typography>
            <Typography
              component="span"
              variant="body2"
              align="left"
              sx={{
                color: COLORS.body,
                fontWeight: isSelected ? 600 : 400,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {node.name}
            </Typography>
          </>
        )}
      </Box>
      {isExpanded &&
        (node.children || []).map((child) => (
          <TreeNode
            key={child.id}
            node={child}
            depth={depth + 1}
            selected={selected}
            expanded={expanded}
            collapsedDuringSearch={collapsedDuringSearch}
            autoExpandedIds={autoExpandedIds}
            visibleIds={visibleIds}
            search={search}
            onSelect={onSelect}
            onCheck={onCheck}
            onToggle={onToggle}
            mode={mode}
            checked={checked}
          />
        ))}
    </Box>
  );
});

TreeNode.propTypes = {
  node: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    level: PropTypes.number,
    children: PropTypes.array,
  }).isRequired,
  depth: PropTypes.number.isRequired,
  selected: PropTypes.string,
  expanded: PropTypes.instanceOf(Set),
  collapsedDuringSearch: PropTypes.instanceOf(Set),
  autoExpandedIds: PropTypes.instanceOf(Set),
  visibleIds: PropTypes.instanceOf(Set),
  search: PropTypes.string,
  onSelect: PropTypes.func,
  onCheck: PropTypes.func,
  onToggle: PropTypes.func,
  mode: PropTypes.string,
  checked: PropTypes.instanceOf(Set),
};

TreeNode.defaultProps = {
  selected: undefined,
  expanded: undefined,
  collapsedDuringSearch: undefined,
  autoExpandedIds: undefined,
  visibleIds: undefined,
  search: "",
  onSelect: undefined,
  onCheck: undefined,
  onToggle: undefined,
  mode: "browse",
  checked: undefined,
};

export const LocationTree = ({
  locations,
  search,
  selected,
  onSelect,
  onCheck,
  checked,
  mode,
  expanded,
  onToggle,
}) => {
  const searchIndex = useMemo(() => buildSearchIndex(locations), [locations]);
  const queryCacheRef = useRef(new Map());
  const [collapsedDuringSearch, setCollapsedDuringSearch] = useState(new Set());

  useEffect(() => {
    queryCacheRef.current.clear();
  }, [searchIndex]);
  useEffect(() => {
    if ((search || "").trim()) return;
    setCollapsedDuringSearch(new Set());
  }, [search]);

  const searchTreeState = useMemo(
    () => computeSearchTreeState(searchIndex, search, queryCacheRef.current),
    [searchIndex, search]
  );
  const searchVisibleIds = searchTreeState.visibleIds;
  const autoExpandedIds = searchTreeState.autoExpandedIds;
  const hasQuery = (search || "").trim().length > 0;
  const handleToggle = useCallback(
    (id, meta) => {
      const safeMeta = meta ?? { isAutoExpanded: false, isExplicitExpanded: false };
      if (hasQuery && safeMeta.isAutoExpanded && !safeMeta.isExplicitExpanded) {
        setCollapsedDuringSearch((previous) => {
          const next = new Set(previous);
          if (next.has(id)) next.delete(id);
          else next.add(id);
          return next;
        });
        return;
      }

      setCollapsedDuringSearch((previous) => {
        if (!previous.has(id)) return previous;
        const next = new Set(previous);
        next.delete(id);
        return next;
      });
      onToggle?.(id);
    },
    [hasQuery, onToggle]
  );

  return (
    <Box>
      {locations.map((location) => (
        <TreeNode
          key={location.id}
          node={location}
          depth={0}
          selected={selected}
          expanded={expanded}
          collapsedDuringSearch={collapsedDuringSearch}
          autoExpandedIds={autoExpandedIds}
          visibleIds={searchVisibleIds}
          search={search}
          onSelect={onSelect}
          onCheck={onCheck}
          onToggle={handleToggle}
          mode={mode}
          checked={checked}
        />
      ))}
    </Box>
  );
};

LocationTree.propTypes = {
  locations: PropTypes.array.isRequired,
  search: PropTypes.string,
  selected: PropTypes.string,
  onSelect: PropTypes.func,
  onCheck: PropTypes.func,
  checked: PropTypes.instanceOf(Set),
  mode: PropTypes.string,
  expanded: PropTypes.instanceOf(Set),
  onToggle: PropTypes.func,
};

LocationTree.defaultProps = {
  search: "",
  selected: undefined,
  onSelect: undefined,
  onCheck: undefined,
  checked: undefined,
  mode: "browse",
  expanded: undefined,
  onToggle: undefined,
};
